'use client';

import React, { useCallback, useContext, useRef, useState } from 'react';
import { useViewportImpressionOnce } from '@/hooks/use-viewport-impression-once';
import { logAidaptiveEvent } from '@/utils/aidaptive-analytics';
import type { AidaptiveContext } from '@/utils/aidaptive-analytics';
import { ImageSrcSetSize } from '@/utils/image';
import { getListingPath } from '@/utils/paths';
import {
  Listing,
  ListingLos,
} from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';
import { differenceInDays } from 'date-fns';
import { ClientBrandConfig } from 'types/brand-config';

import { AidaptiveImpressionPixel } from '@/components/ui/aidaptive-impression-pixel';
import { ListingImages } from '@/components/ui/listing-images';
import { ListingBedBathGuestDescription } from '@/components/listing/listing-bed-bath-guest-description';
import ListingViewDescription from '@/components/listing/listing-description';
import { MapContext } from '@/components/map/control';

import ListingRate from './listing-rate';
import './listings.css';

export interface ListingCardProps {
  id: string;
  listing: Listing;
  listingLos?: ListingLos;
  className?: string;
  imageSizes?: ImageSrcSetSize[];
  brand: ClientBrandConfig;
  searchParams?: {
    arrive?: string;
    depart?: string;
    guests?: string;
    adults?: string;
    children?: string;
    flex_weeks?: string;
    flex_days?: string;
  };
  currentListing?: boolean;
  onFavoriteClick?: (listingId: string) => void;
  clickTrackerUrl?: string | null;
  impressionUrl?: string | null;
  aidaptiveContext?: AidaptiveContext;
}

export const ListingCard: React.FC<ListingCardProps> = ({
  id,
  listing,
  listingLos,
  imageSizes,
  className,
  brand,
  searchParams,
  currentListing,
  onFavoriteClick,
  clickTrackerUrl,
  impressionUrl,
  aidaptiveContext,
}) => {
  const { setHoveredListing, hoveredListing, setCurrentListing } =
    useContext(MapContext);
  const [listingUrl, setListingUrl] = useState<string>(
    getListingPath(listing, brand)
  );
  const cardRef = useRef<HTMLDivElement>(null);
  const impressionFiredRef = useRef(false);
  const [impressionRecorded, setImpressionRecorded] = useState(false);

  const handleImpression = useCallback(() => {
    if (!aidaptiveContext || impressionFiredRef.current) return;
    impressionFiredRef.current = true;
    logAidaptiveEvent({
      productId: listing.id,
      service: aidaptiveContext.service,
      eventType: 'impression',
      pageType: aidaptiveContext.pageType,
      customerCohort: aidaptiveContext.customerCohort,
    });
    setImpressionRecorded(true);
  }, [aidaptiveContext, listing.id]);

  useViewportImpressionOnce(cardRef, handleImpression, {
    enabled: !!aidaptiveContext && !!impressionUrl,
  });

  const handleAidaptiveClick = () => {
    if (!aidaptiveContext) return;
    logAidaptiveEvent({
      productId: listing.id,
      service: aidaptiveContext.service,
      eventType: 'click',
      pageType: aidaptiveContext.pageType,
      customerCohort: aidaptiveContext.customerCohort,
    });
  };

  const labels: string[] = [];
  if (
    listing.effective_date &&
    brand.newListingDays &&
    differenceInDays(new Date(), listing.effective_date) < brand.newListingDays
  ) {
    labels.push('New!');
  }

  const clickUrl = clickTrackerUrl
    ? (() => {
        const url = new URL(clickTrackerUrl);
        const destination = `${window.location.origin}${listingUrl}`;
        url.searchParams.set('destination_url', destination);
        return url.toString();
      })()
    : null;

  return (
    <div
      ref={cardRef}
      onMouseEnter={(e) => {
        e.preventDefault();
        setHoveredListing(listing);
        setCurrentListing(null);
      }}
      onTouchStart={(e) => {
        e.preventDefault();
        setHoveredListing(listing);
        setCurrentListing(null);
      }}
      id={id}
      className={clsx(
        'listing-card cursor-pointer',
        className,
        currentListing && 'map-selected',
        hoveredListing?.id === listing.id && 'map-hovered',
        // showMap && 'margin-map',
        'hover:shadow-md'
      )}
      key={listing.id}
    >
      {listing.images?.[0] && (
        <div className="image-cover relative" onClick={handleAidaptiveClick}>
          {listing.images && (
            <div draggable={false}>
              <ListingImages
                onFavoriteClick={onFavoriteClick}
                listingId={listing.id}
                imageSizes={imageSizes}
                images={listing.images}
                listingUrl={listingUrl.toString()}
                clickTrackerUrl={clickUrl}
                label={labels.join(', ')}
              />
            </div>
          )}
        </div>
      )}
      <div
        className="listing-card-content"
        onClick={() => {
          handleAidaptiveClick();
          window.open(clickUrl || listingUrl.toString(), '_blank');
        }}
      >
        <div className="card-title">
          {brand.showResortInSearchResults && (
            <span className="pre-header-link caps-sm text-[.75rem]">
              {listing.resort_name}
            </span>
          )}
          <h6 className="header-link">
            {listing.marketing?.short_name ||
              listing.marketing?.common_name ||
              listing.name_detailed}
          </h6>
        </div>
        <div className="card-content">
          <div className="flex flex-col gap-[0.25rem]">
            <ListingBedBathGuestDescription
              brand={brand}
              size="sm"
              listing={listing}
            />
            <ListingViewDescription
              displayUnitView={brand.showListingView}
              listing={listing}
            />
          </div>
        </div>
        <ListingRate
          setListingUrl={setListingUrl}
          brand={brand}
          listingLos={listingLos}
          listing={listing}
          hasFlexableRates={
            !!searchParams?.flex_days || !!searchParams?.flex_weeks
          }
        />
      </div>
      {impressionRecorded && impressionUrl && (
        <AidaptiveImpressionPixel url={impressionUrl} />
      )}
    </div>
  );
};

export default ListingCard;
